import {
  DragDropProvider,
  DragHandle,
  DropTarget,
} from '@wandb/weave/common/containers/DragDropContainer';
import React, {useCallback} from 'react';

import {useApolloClient} from '../../state/hooks';
import * as ViewApi from '../../state/views/api';
import {ReorderFn, useDragDropReorder} from '../../util/dragDrop';
import {imgSrcWithDefault} from '../../util/images';
import {ShowcasedReport} from './context';
import * as S from './ReportShowcaseCards.styles';

const ITEM_DROPZONE_PADDING_PX = 8;

type ReportShowcaseCardsProps = {
  items: ShowcasedReport[];
  reorderItems: ReorderFn;
  readOnly: boolean;
};

export const ReportShowcaseCards: React.FC<ReportShowcaseCardsProps> =
  React.memo(({items, reorderItems, readOnly}) => {
    const client = useApolloClient();
    const savePreviewImage = useCallback(
      (id: string, previewURL: string) =>
        ViewApi.save(client, {
          id,
          previewUrl: previewURL,
        }),
      [client]
    );

    const {
      makeDragSourceCallbackRef,
      renderDropIndicators,
      onDragOver,
      onDragEnd,
      onDrop,
    } = useDragDropReorder({
      reorder: reorderItems,
      dropzonePadding: ITEM_DROPZONE_PADDING_PX,
    });

    return (
      <DragDropProvider>
        <DropTarget
          partRef={{id: `target`}}
          onDragOver={onDragOver}
          onDrop={onDrop}>
          <S.Grid>
            {items.map(
              (
                {id, url, name, description, imageURL, lastEdit, viewCount},
                i
              ) => (
                <S.Card key={id} to={url} ref={makeDragSourceCallbackRef(i)}>
                  <S.CardContent
                    partRef={{id: `${i}`}}
                    draggingStyle={{opacity: 0.25}}
                    onDragEnd={onDragEnd}>
                    {renderDropIndicators(i)}
                    <S.CardImg
                      photoUrl={imgSrcWithDefault(imageURL)}
                      displaySize={S.CARD_IMG_SIZE_PX}
                      label={false}
                      readOnly={readOnly}
                      viewID={id}
                      updateOverlay="Update Preview"
                      updateOverlaySize={`small`}
                      save={previewURL => savePreviewImage(id, previewURL)}
                    />
                    <S.CardText>
                      <S.CardHeader>{name}</S.CardHeader>
                      <S.CardDescription>{description}</S.CardDescription>
                      <S.CardMeta>
                        <S.CardMetaLeft>
                          <S.ViewsIcon />{' '}
                          <S.ClampedLine>{viewCount} views</S.ClampedLine>
                        </S.CardMetaLeft>
                        <S.CardMetaRight>
                          <S.UpdatedAtIcon />{' '}
                          <S.ClampedLine>Last edit {lastEdit}</S.ClampedLine>
                        </S.CardMetaRight>
                      </S.CardMeta>
                    </S.CardText>
                    {!readOnly && (
                      <S.CardActions>
                        <DragHandle partRef={{id: `${i}`}}>
                          <S.DragHandle />
                        </DragHandle>
                      </S.CardActions>
                    )}
                  </S.CardContent>
                </S.Card>
              )
            )}
          </S.Grid>
        </DropTarget>
      </DragDropProvider>
    );
  });
